import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Users, ArrowRight, Loader2, Check, ShieldCheck, FileDown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const INPUT = "w-full bg-navy border border-white/15 text-white px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors";

const BENEFITS = [
  "A turnkey 8-week program your service members can complete before separation",
  "One facilitator can support an entire cohort — the AI strategist does the heavy lifting",
  "Each participant leaves with a real, launched business — not just a handout",
  "Simple onboarding for your transition office, VSO or resource program",
];

export default function CohortRequest() {
  const [f, setF] = useState({ org_name: "", contact_name: "", email: "", phone: "", role: "", installation: "", cohort_size: "", timeframe: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [dl, setDl] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const downloadOnePager = async () => {
    setDl(true);
    try {
      const r = await api.get("/cohort/one-pager", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "operation-second-mission-overview.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download — please try again."); } finally { setDl(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.org_name.trim() || !f.contact_name.trim() || !f.email.trim()) { toast.error("Please add your organization, name and email."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) { toast.error("Please enter a valid email address."); return; }
    setBusy(true);
    try {
      await api.post("/cohort-requests", f);
      setDone(true);
      toast.success("Request received — we'll be in touch.");
    } catch (err) { toast.error(formatApiError(err) || "Something went wrong — please try again."); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="cohort-request-page">
      <section className="relative border-b border-white/10 overflow-hidden grain">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 relative">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <a href="/veterans/transitioning" className="text-gray-400 hover:text-gold text-xs mb-4 inline-block transition-colors" data-testid="cohort-back">← Transitioning service members</a>
            <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-5"><Users size={15} /> For transition offices, VSOs &amp; resource partners</div>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light text-white leading-[1.05] max-w-3xl">
              Bring a cohort to your <span className="text-gold">installation or program.</span>
            </h1>
            <p className="text-base md:text-lg text-gray-300 font-light mt-6 max-w-2xl leading-relaxed">
              Run the Operation Second Mission launch program with a group of your transitioning service members. Tell us a little about your organization and we'll set up a call.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="font-heading text-2xl text-white mb-5">What partnering looks like</h2>
            <div className="space-y-4">
              {BENEFITS.map((b) => (
                <div key={b} className="flex items-start gap-3 text-gray-200"><ShieldCheck size={18} className="text-gold shrink-0 mt-0.5" /><span className="text-sm md:text-base">{b}</span></div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="/veterans/cohort/overview" data-testid="cohort-view-overview"
                className="inline-flex items-center gap-2 text-sm bg-gold/15 text-gold border border-gold/30 px-5 py-2.5 hover:bg-gold/25 transition-colors">
                <ShieldCheck size={15} /> View interactive overview
              </a>
              <button onClick={downloadOnePager} disabled={dl} data-testid="cohort-onepager"
                className="inline-flex items-center gap-2 text-sm border border-white/15 text-white px-5 py-2.5 hover:border-gold/50 transition-colors disabled:opacity-60">
                {dl ? <Loader2 className="animate-spin" size={15} /> : <FileDown size={15} />} Download program overview (PDF)
              </button>
            </div>
          </div>

          {done ? (
            <div className="border border-gold/40 bg-gold/10 p-8 text-center" data-testid="cohort-success">
              <div className="w-14 h-14 border border-gold/40 rounded-full flex items-center justify-center text-gold mx-auto mb-4"><Check size={26} /></div>
              <h3 className="font-heading text-2xl text-white mb-2">Request received</h3>
              <p className="text-gray-300 text-sm">Thank you — we'll reach out shortly to talk through running a cohort for your service members.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-white/10 bg-navy-800 p-6 space-y-4" data-testid="cohort-form">
              <h3 className="font-heading text-xl text-white">Request a cohort</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="cohort-org" className={INPUT} placeholder="Organization / installation *" value={f.org_name} onChange={(e) => set("org_name", e.target.value)} />
                <input data-testid="cohort-name" className={INPUT} placeholder="Your name *" value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="cohort-email" type="email" className={INPUT} placeholder="Email *" value={f.email} onChange={(e) => set("email", e.target.value)} />
                <input data-testid="cohort-phone" className={INPUT} placeholder="Phone (optional)" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="cohort-role" className={INPUT} placeholder="Your role (e.g. TAP counselor)" value={f.role} onChange={(e) => set("role", e.target.value)} />
                <input data-testid="cohort-installation" className={INPUT} placeholder="Base / location" value={f.installation} onChange={(e) => set("installation", e.target.value)} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="cohort-size" className={INPUT} placeholder="Approx. cohort size" value={f.cohort_size} onChange={(e) => set("cohort_size", e.target.value)} />
                <input data-testid="cohort-timeframe" className={INPUT} placeholder="Ideal timeframe" value={f.timeframe} onChange={(e) => set("timeframe", e.target.value)} />
              </div>
              <textarea data-testid="cohort-message" className={`${INPUT} resize-none`} rows={3} placeholder="Anything else we should know? (optional)" value={f.message} onChange={(e) => set("message", e.target.value)} />
              <button type="submit" disabled={busy} data-testid="cohort-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-navy px-6 py-3.5 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
                {busy ? <Loader2 className="animate-spin" size={16} /> : <>Request a cohort <ArrowRight size={16} /></>}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
